import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { trackQuantifyAnomaliesEvent } from '@modules/quantify/components/QuantifyAnomaliesToggle/QuantifyAnomaliesToggle';
import {
  ANOMALY_HELPCENTER_URL,
  DEFAULT_ANOMALY_THRESHOLD,
  MAX_ANOMALY_THRESHOLD,
  MIN_ANOMALY_THRESHOLD,
  ZSCORE_HELPCENTER_URL,
} from '@modules/quantify/constants';
import { useFetchQuantifyReport } from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { SettingsRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { QuantifyVisualisationType } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { shallow } from 'zustand/shallow';

interface QuantifyAnomaliesThresholdProps {
  isDisabled?: boolean;
}

export const QuantifyAnomaliesThreshold = ({
  isDisabled,
}: QuantifyAnomaliesThresholdProps) => {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const anomalyThreshold = useQuantifyStore(
    ({ query }) => query.anomalyThreshold,
  );
  const shouldShowAnomalies = useQuantifyStore(
    ({ query }) => query.shouldShowAnomalies,
  );
  const isCustomAnomalyThresholdSelected = useQuantifyStore(
    ({ visual }) => visual.isCustomAnomalyThresholdSelected,
  );
  const groupByLabel = useQuantifyStore(
    ({ query }) => query.groupByField?.displayName,
  );

  const [popoverAnchorEl, setPopoverAnchorEl] =
    useState<null | HTMLButtonElement>(null);

  const [customThreshold, setCustomThreshold] = useState(anomalyThreshold);
  const [isOutOfRangeError, setIsOutOfRangeError] = useState(false);

  const fetchQuantifyReport = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const { title: name, href } = useAnalysisStore(
    ({ title, href, scope }) => ({ title, href, scope }),
    shallow,
  );

  useEffect(() => {
    setCustomThreshold(anomalyThreshold);
  }, [anomalyThreshold]);

  useUpdateEffect(() => {
    if (!isCustomAnomalyThresholdSelected) {
      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: {
          isCustomAnomalyThresholdSelected:
            anomalyThreshold !== DEFAULT_ANOMALY_THRESHOLD,
        },
      });
    }
  }, [anomalyThreshold]);

  useUpdateEffect(() => {
    if (shouldFetchReportData) {
      setShouldFetchReportData(false);
      fetchQuantifyReport();
    }
  }, [shouldFetchReportData]);

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchorEl(event.currentTarget);
    trackQuantifyAnomaliesEvent({
      eventName: 'Quantify Anomalies - Set Threshold Popover Opened',
      query: {
        showMeLabel: groupByLabel,
      },
      name,
      link: href,
    });
  };

  const closePopover = () => {
    setPopoverAnchorEl(null);
    setCustomThreshold(anomalyThreshold);
    trackQuantifyAnomaliesEvent({
      eventName: 'Quantify Anomalies - Set Threshold Popover Closed',
      query: {
        showMeLabel: groupByLabel,
      },
      name,
      link: href,
    });
  };

  const handleApplyThreshold = () => {
    const threshold = isCustomAnomalyThresholdSelected
      ? customThreshold ?? DEFAULT_ANOMALY_THRESHOLD
      : DEFAULT_ANOMALY_THRESHOLD;

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        anomalyThreshold: threshold,
        visualisationType: QuantifyVisualisationType.Trend,
      },
    });
    setShouldFetchReportData(true);
    closePopover();

    trackQuantifyAnomaliesEvent({
      eventName: 'Quantify Anomalies - Apply Threshold Button Clicked',
      query: {
        showMeLabel: groupByLabel,
      },
      name,
      link: href,
    });
  };

  const hasApplied =
    shouldShowAnomalies && anomalyThreshold === customThreshold;

  return (
    <Box>
      <Tooltip arrow title="Configure anomaly threshold" placement="top">
        <IconButton
          color="primary"
          size="small"
          onClick={openPopover}
          disabled={isDisabled}
          aria-label="anomalies-configure-threshold-button"
        >
          <SettingsRounded />
        </IconButton>
      </Tooltip>
      <Popover
        disablePortal
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 24,
        }}
        data-role="anomalies-configure-threshold-popover"
      >
        <Stack width="300px" spacing={2} p={1.5}>
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="subtitle1">Set Threshold</Typography>
              <Typography variant="caption" color="text.secondary">
                (By z-score.{' '}
                <Link
                  href={ZSCORE_HELPCENTER_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more.
                </Link>
                )
              </Typography>
            </Stack>
            <Divider />
          </Box>
          <RadioGroup>
            <FormControlLabel
              checked={!isCustomAnomalyThresholdSelected}
              onChange={(_, checked) => {
                dispatch({
                  type: QuantifyActionType.SET_VISUAL,
                  payload: {
                    isCustomAnomalyThresholdSelected: !checked,
                  },
                });
                if (checked) {
                  setCustomThreshold(DEFAULT_ANOMALY_THRESHOLD);
                  trackQuantifyAnomaliesEvent({
                    eventName:
                      'Quantify Anomalies - Default Threshold Option Selected',
                    query: {
                      showMeLabel: groupByLabel,
                    },
                    name,
                    link: href,
                    anomalyThreshold: DEFAULT_ANOMALY_THRESHOLD,
                  });
                }
              }}
              control={<Radio size="small" />}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2">Default</Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Recommended)
                  </Typography>
                </Stack>
              }
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                checked={isCustomAnomalyThresholdSelected}
                onChange={(_, checked) => {
                  dispatch({
                    type: QuantifyActionType.SET_VISUAL,
                    payload: {
                      isCustomAnomalyThresholdSelected: checked,
                    },
                  });

                  if (checked) {
                    trackQuantifyAnomaliesEvent({
                      eventName:
                        'Quantify Anomalies - Custom Threshold Option Selected',
                      query: {
                        showMeLabel: groupByLabel,
                      },
                      name,
                      link: href,
                      anomalyThreshold,
                    });
                  }
                }}
                control={<Radio size="small" />}
                label={<Typography variant="subtitle2">Custom</Typography>}
              />
              <TextField
                type="number"
                size="small"
                value={customThreshold}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setCustomThreshold(value);
                  setIsOutOfRangeError(
                    value < MIN_ANOMALY_THRESHOLD ||
                      value > MAX_ANOMALY_THRESHOLD,
                  );
                  trackQuantifyAnomaliesEvent({
                    eventName:
                      'Quantify Anomalies - Custom Threshold Input Value Changed',
                    query: {
                      showMeLabel: groupByLabel,
                    },
                    name,
                    link: href,
                    anomalyThreshold: value,
                  });
                }}
                inputProps={{
                  step: 0.1,
                  min: MIN_ANOMALY_THRESHOLD,
                  max: MAX_ANOMALY_THRESHOLD,
                }}
                disabled={!isCustomAnomalyThresholdSelected}
                error={isOutOfRangeError}
                helperText={
                  isOutOfRangeError
                    ? `Allowed Range: ${MIN_ANOMALY_THRESHOLD} - ${MAX_ANOMALY_THRESHOLD}`
                    : ''
                }
                placeholder="Enter threshold"
              />
            </Stack>
          </RadioGroup>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApplyThreshold}
            disabled={isOutOfRangeError || hasApplied}
            aria-label={hasApplied ? 'Applied' : 'Apply'}
          >
            {hasApplied ? 'Applied' : 'Apply'}
          </Button>
          <Stack spacing={1} lineHeight="6px">
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Filter data to show only unusual trends. A trend is anomalous if
              it has a z-score higher than the set threshold.{' '}
              <Link
                href={ANOMALY_HELPCENTER_URL}
                target="_blank"
                rel="noreferrer"
              >
                Learn more.
              </Link>
            </Typography>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};
